import React, { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import Mascot from "@/components/Mascot";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PasswordInput } from "@/components/PasswordInput";
import { formatApiErrorDetail } from "@/lib/api";
import { GraduationCap, Star, ArrowLeft, Loader2, Sparkles } from "lucide-react";

const MOTIVASI = [
  "🚀 Masa depan hebat dimulai dari semangat belajar hari ini!",
  "🌟 Tidak harus sempurna, yang penting terus berkembang!",
  "💡 Setiap pertanyaan adalah awal dari pengetahuan baru.",
  "🏆 Kesuksesan bukan tentang siapa yang tercepat, tetapi siapa yang terus berusaha.",
  "🎯 Belajar tanpa batas, berkembang tanpa henti!",
];

export default function Login() {
  const { login } = useAuth();
  const quote = useMemo(() => MOTIVASI[Math.floor(Math.random() * MOTIVASI.length)], []);
  const navigate = useNavigate();
  const [role, setRole] = useState(null); // guru | murid
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const u = await login(username, password, role);
      navigate(u.role === "guru" ? "/guru" : "/murid");
    } catch (e) {
      if (e.message === "role-mismatch") {
        setError(`Akun ini adalah akun ${e.role === "guru" ? "guru" : "murid"}, bukan ${role === "guru" ? "guru" : "murid"}. Silakan pilih peran yang benar.`);
      } else {
        setError(formatApiErrorDetail(e.response?.data?.detail) || "Gagal masuk");
      }
      setLoading(false);
    }
  };

  return (
    <div className="grain min-h-screen flex flex-col lg:flex-row">
      {/* left hero */}
      <div className="lg:w-1/2 bg-teal-600 text-white relative overflow-hidden flex flex-col justify-center px-8 sm:px-14 py-14">
        <div className="absolute -top-16 -right-16 w-72 h-72 rounded-full bg-teal-500/50" />
        <div className="absolute bottom-10 -left-10 w-52 h-52 rounded-full bg-amber-400/30" />
        <div className="relative z-10 max-w-md" data-testid="login-hero">
          <Mascot size={110} className="animate-float mb-6" />
          <p className="font-display text-amber-300 font-semibold tracking-wide mb-2" data-testid="hero-tagline">
            Belajar Hari Ini, Hebat di Masa Depan!
          </p>
          <h1 className="font-display text-4xl sm:text-5xl font-bold leading-tight">DUNIA KELAS</h1>
          <p className="mt-3 text-base sm:text-lg text-white font-semibold" data-testid="hero-welcome">
            Selamat Datang, Generasi Hebat! 🚀
          </p>
          <p className="mt-3 text-base text-teal-50 leading-relaxed">
            Setiap langkah kecil dalam belajar adalah awal dari pencapaian besar. Jangan takut mencoba,
            jangan berhenti bertanya, dan jangan pernah menyerah. Teruslah belajar, berkembang, dan wujudkan
            impianmu. Masa depan dimulai dari apa yang kamu pelajari hari ini!
          </p>
          <div className="mt-6 flex items-start gap-2 text-sm text-white bg-white/10 rounded-2xl px-4 py-3 w-fit backdrop-blur-sm animate-pop" data-testid="hero-quote">
            <Sparkles className="w-4 h-4 text-amber-300 mt-0.5 shrink-0" />
            <span className="italic">{quote}</span>
          </div>
          <p className="mt-6 text-sm text-teal-100 font-medium" data-testid="hero-bottom">
            🌟 Satu Tempat Belajar, Sejuta Impian untuk Masa Depan.
          </p>
          <p className="mt-1 font-display text-amber-200 font-semibold" data-testid="hero-slogan">
            “Belajar Tanpa Batas, Raih Masa Depan Berkualitas.”
          </p>
        </div>
      </div>

      {/* right form */}
      <div className="lg:w-1/2 flex items-center justify-center px-6 py-14 relative z-[2]">
        <div className="w-full max-w-sm">
          {!role ? (
            <div className="animate-pop">
              <h2 className="font-display text-3xl font-bold text-slate-800">Selamat Datang!</h2>
              <p className="text-slate-500 mt-2">Pilih cara kamu masuk ke DUNIA KELAS.</p>
              <div className="mt-8 space-y-4">
                <button onClick={() => setRole("guru")} data-testid="choose-guru-btn"
                  className="w-full flex items-center gap-4 p-5 rounded-2xl border-2 border-slate-200 hover:border-teal-500 hover:bg-teal-50/50 transition-colors text-left group">
                  <span className="w-14 h-14 rounded-2xl bg-teal-100 text-teal-700 flex items-center justify-center group-hover:scale-105 transition-transform">
                    <GraduationCap className="w-7 h-7" />
                  </span>
                  <span>
                    <span className="block font-display font-semibold text-lg text-slate-800">Masuk sebagai Guru</span>
                    <span className="block text-sm text-slate-500">Kelola murid, tugas & penilaian</span>
                  </span>
                </button>
                <button onClick={() => setRole("murid")} data-testid="choose-murid-btn"
                  className="w-full flex items-center gap-4 p-5 rounded-2xl border-2 border-slate-200 hover:border-amber-500 hover:bg-amber-50/50 transition-colors text-left group">
                  <span className="w-14 h-14 rounded-2xl bg-amber-100 text-amber-600 flex items-center justify-center group-hover:scale-105 transition-transform">
                    <Star className="w-7 h-7" />
                  </span>
                  <span>
                    <span className="block font-display font-semibold text-lg text-slate-800">Masuk sebagai Murid</span>
                    <span className="block text-sm text-slate-500">Absen, kerjakan tugas & lihat nilai</span>
                  </span>
                </button>
              </div>
            </div>
          ) : (
            <form onSubmit={submit} className="animate-pop">
              <button type="button" onClick={() => { setRole(null); setError(""); }} className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-700 mb-6" data-testid="back-btn">
                <ArrowLeft className="w-4 h-4" /> Kembali
              </button>
              <h2 className="font-display text-3xl font-bold text-slate-800">
                Masuk sebagai {role === "guru" ? "Guru" : "Murid"}
              </h2>
              <p className="text-slate-500 mt-2">Masukkan username dan kata sandi kamu.</p>

              <div className="mt-7 space-y-4">
                <div>
                  <Label htmlFor="username" className="text-slate-700">Username</Label>
                  <Input id="username" value={username} onChange={(e) => setUsername(e.target.value)} required
                    className="mt-1.5 h-12 rounded-xl" placeholder="contoh: andi" data-testid="login-username-input" />
                </div>
                <div>
                  <Label htmlFor="password" className="text-slate-700">Kata Sandi</Label>
                  <PasswordInput id="password" value={password} onChange={(e) => setPassword(e.target.value)} required
                    className="mt-1.5 h-12 rounded-xl" placeholder="••••••••" data-testid="login-password-input" />
                </div>
                {error && <p className="text-sm text-rose-600 bg-rose-50 rounded-xl px-4 py-2.5" data-testid="login-error">{error}</p>}
                <Button type="submit" disabled={loading} data-testid="login-submit-btn"
                  className={`w-full h-12 rounded-xl text-base font-semibold ${role === "guru" ? "bg-teal-600 hover:bg-teal-700" : "bg-amber-500 hover:bg-amber-600"}`}>
                  {loading && <Loader2 className="w-5 h-5 mr-2 animate-spin" />} Masuk
                </Button>
              </div>

              <p className="mt-6 text-xs text-slate-400 leading-relaxed">
                {role === "guru"
                  ? "Coba: gurubudi / guru12345"
                  : "Coba: budis / budi123 (Kelas 3). Akun murid dibuat oleh guru."}
              </p>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
